/**
 * The developer console: what the platform actually did, for whoever is integrating against it.
 *
 * An integration conversation has one recurring shape — "I sent a message and something
 * unexpected came back, why?" — and answering it needs the turn's whole story. That story is
 * already in the audit row, because rule 14 required it before anyone asked for a console; this
 * screen renders it.
 *
 * ## Three panels, in the order a question gets asked
 *
 * **Systems** first, because "is it just me?" is the first question and a dead dependency answers
 * most of them. **The last hour** next, because a refusal rate that has moved is a different
 * conversation from one turn that went wrong. **The feed** last, expandable per turn, because by
 * then you know which turn you are looking for.
 *
 * ## The feed polls, and it polls incrementally
 *
 * `sinceId` asks for turns newer than the newest one held, so a console left open on a wall
 * display does not re-fetch its own history every few seconds. Event ids are ULIDs and therefore
 * time-ordered, which is what makes "newer than" a cursor rather than a guess — ordering by
 * timestamp would tie on two turns in the same millisecond and a poll would then skip one or
 * repeat it.
 *
 * ## What is on screen is masked
 *
 * Every message here is the masked form, because that is the only form the audit store ever held.
 * An account number a customer typed reads `«number»` because it was `«number»` when it was
 * written. That is not a reason to leave the screen open: a masked transcript of real
 * conversations is still a transcript of real conversations, which is why every route behind it
 * requires a signed-in viewer.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  StatusPill,
  directionOfText,
  fetchDevSummary,
  fetchDevSystems,
  fetchDevTurns,
  postmanCollectionUrl,
  useToast,
  type DevSummary,
  type DevSystems,
  type DevTurn,
} from "@nab/shared";

import { VersionBar } from "./VersionBar";

/** How often the feed asks for new turns while it is on screen. */
const POLL_MS = 4000;

/** The feed is capped so a console left open overnight does not grow without bound. */
const MAX_TURNS = 200;

const STEP_TONE: Record<string, "success" | "neutral" | "warning" | "danger"> = {
  reply: "success",
  trigger: "success",
  continue: "neutral",
  refuse: "warning",
};

export function DevelopersPage(): JSX.Element {
  const { t } = useTranslation("app");
  const toast = useToast();
  const [turns, setTurns] = useState<DevTurn[]>([]);
  const [summary, setSummary] = useState<DevSummary | null>(null);
  const [systems, setSystems] = useState<DevSystems | null>(null);
  const [live, setLive] = useState(true);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [target, setTarget] = useState("http://127.0.0.1:8000");
  const newest = useRef<string | null>(null);
  // Which question the feed is currently asking. A poll that was in flight when the filter changed
  // is answering the previous one, and its rows must not be mixed in: the user sees refusals only,
  // and a reply from the old request arrives a moment later and sits in the list looking like a
  // filter that does not work.
  const asked = useRef(0);

  const poll = useCallback(async () => {
    const generation = asked.current;
    try {
      const { turns: fresh } = await fetchDevTurns({
        limit: 50,
        ...(newest.current ? { sinceId: newest.current } : {}),
        ...(filter ? { nextStep: filter } : {}),
      });
      if (generation !== asked.current) return;
      if (fresh.length > 0) {
        newest.current = fresh[0]!.auditId;
        setTurns((current) => {
          // Dropped by id as well, because the generation check above cannot catch an overlap
          // within one question: two polls racing on the same filter both read `newest` before
          // either writes it, and both then prepend the same turn. Two copies of one turn is the
          // kind of thing an integrator reasonably reads as the bot having handled their message
          // twice.
          const held = new Set(current.map((entry) => entry.auditId));
          const added = fresh.filter((entry) => !held.has(entry.auditId));
          if (added.length === 0) return current;
          return [...added, ...current].slice(0, MAX_TURNS);
        });
      }
    } catch {
      // A failed poll is a blip; the next one will catch up. Reporting each would make a brief
      // network wobble into a wall of toasts on a screen somebody is leaving open.
    }
  }, [filter]);

  // The filter changes what the cursor means, so the feed restarts rather than mixing two
  // filters' results in one list.
  useEffect(() => {
    asked.current += 1;
    newest.current = null;
    setTurns([]);
    void poll();
  }, [filter, poll]);

  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(timer);
  }, [live, poll]);

  const refreshPanels = useCallback(async () => {
    try {
      const [next, checks] = await Promise.all([
        fetchDevSummary(60),
        fetchDevSystems(),
      ]);
      setSummary(next);
      setSystems(checks);
    } catch {
      toast.show({ tone: "error", message: t("dev.panelsFailed") });
    }
  }, [toast, t]);

  useEffect(() => {
    void refreshPanels();
  }, [refreshPanels]);

  return (
    <div className="flex flex-col">
      <VersionBar />
      <div className="flex flex-col gap-4 p-4">
        <p className="max-w-prose text-body-sm text-textMuted">{t("dev.intro")}</p>

        {/* -- systems ---------------------------------------------------------------- */}
        <Card className="flex flex-col gap-3 p-4">
          <div className="flex items-center gap-2">
            <h3 className="text-heading-sm">{t("dev.systems")}</h3>
            {systems ? (
              <StatusPill tone={systems.allUp ? "success" : "danger"}>
                {systems.allUp ? t("dev.allUp") : t("dev.somethingDown")}
              </StatusPill>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              className="ms-auto"
              onClick={refreshPanels}
            >
              {t("dev.refresh")}
            </Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(systems?.checks ?? {}).map(([name, check]) => (
              <div
                key={name}
                className="flex flex-col gap-1 rounded-sm border border-borderSubtle p-3"
              >
                <div className="flex items-center gap-2">
                  <StatusPill
                    tone={check.up === true ? "success" : check.up === false ? "danger" : "neutral"}
                  >
                    {check.up === true
                      ? t("dev.up")
                      : check.up === false
                        ? t("dev.down")
                        : t("dev.unknown")}
                  </StatusPill>
                  <span className="font-mono text-body-sm">{name}</span>
                </div>
                <span className="text-label-md text-textMuted">
                  {/* The endpoint and the profile, never a key. */}
                  {[
                    check.detail,
                    check.profile,
                    check.endpoint,
                    check.version,
                    check.customerCollection
                      ? t("dev.readsOnly", { name: check.customerCollection })
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* -- the hour -------------------------------------------------------------- */}
        {summary ? (
          <Card className="flex flex-col gap-3 p-4">
            <h3 className="text-heading-sm">{t("dev.lastHour")}</h3>
            <div className="flex flex-wrap gap-4">
              <Figure label={t("dev.turns")} value={String(summary.turns)} />
              <Figure
                label={t("dev.refusalRate")}
                value={`${Math.round(summary.refusalRate * 100)}%`}
              />
              <Figure
                label="p50"
                value={summary.p50LatencyMs ? `${Math.round(summary.p50LatencyMs)} ms` : "—"}
              />
              <Figure
                label="p95"
                value={summary.p95LatencyMs ? `${Math.round(summary.p95LatencyMs)} ms` : "—"}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.byIntent).map(([intent, count]) => (
                <StatusPill key={intent} tone="neutral">
                  {intent} · {count}
                </StatusPill>
              ))}
            </div>
          </Card>
        ) : null}

        {/* -- postman --------------------------------------------------------------- */}
        <Card className="flex flex-col gap-3 p-4">
          <h3 className="text-heading-sm">{t("dev.postman")}</h3>
          <p className="max-w-prose text-body-sm text-textMuted">{t("dev.postmanHint")}</p>
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label={t("dev.targetUrl")}
              hint={t("dev.targetUrlHint")}
              className="min-w-[280px]"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
            {/* An anchor rather than a fetch: the response carries Content-Disposition, so the
                browser names the file and offers the save dialog. Same-origin, so the session
                cookie rides along. */}
            <a
              href={postmanCollectionUrl(target)}
              download
              className="rounded-sm bg-primary px-4 py-2 text-body-md text-onPrimary hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {t("dev.download")}
            </a>
          </div>
        </Card>

        {/* -- the feed -------------------------------------------------------------- */}
        <Card className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-heading-sm">{t("dev.feed")}</h3>
            <StatusPill tone={live ? "success" : "neutral"}>
              {live ? t("dev.living") : t("dev.paused")}
            </StatusPill>
            <Select
              label={t("dev.filter")}
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              options={[
                { value: "", label: t("dev.all") },
                { value: "reply", label: "reply" },
                { value: "continue", label: "continue" },
                { value: "trigger", label: "trigger" },
                { value: "refuse", label: "refuse" },
              ]}
            />
            <Button
              size="sm"
              variant="secondary"
              className="ms-auto self-end"
              onClick={() => setLive((current) => !current)}
            >
              {live ? t("dev.pause") : t("dev.resume")}
            </Button>
          </div>

          {turns.length === 0 ? (
            <EmptyState title={t("dev.emptyTitle")} description={t("dev.emptyBody")} />
          ) : (
            <ul className="flex flex-col gap-2">
              {turns.map((turn) => (
                <TurnRow
                  key={turn.auditId}
                  turn={turn}
                  open={open === turn.auditId}
                  onToggle={() =>
                    setOpen((current) => (current === turn.auditId ? null : turn.auditId))
                  }
                />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-sm border border-borderSubtle px-4 py-2">
      <span className="block text-heading-sm font-variant-numeric tabular-nums">{value}</span>
      <span className="text-label-md text-textMuted">{label}</span>
    </div>
  );
}

function TurnRow({
  turn,
  open,
  onToggle,
}: {
  turn: DevTurn;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { t } = useTranslation("app");

  return (
    <li className="rounded-sm border border-borderSubtle">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-3 px-3 py-2 text-start hover:bg-surfaceSunken"
      >
        <span className="font-mono text-label-md text-textMuted">
          {turn.at.slice(11, 19)}
        </span>
        <StatusPill tone={STEP_TONE[turn.nextStep ?? ""] ?? "neutral"}>
          {turn.nextStep ?? "?"}
        </StatusPill>
        <span className="font-mono text-label-md">{turn.intent ?? "—"}</span>
        <span
          dir={directionOfText(turn.message ?? "")}
          className="min-w-0 flex-1 truncate text-body-sm text-textMuted [unicode-bidi:isolate]"
        >
          {turn.message}
        </span>
        <span className="font-mono text-label-md tabular-nums text-textMuted">
          {turn.latencyMs ? `${Math.round(turn.latencyMs)} ms` : ""}
        </span>
        {turn.routerFailed ? (
          <StatusPill tone="danger">{t("dev.routerFailed")}</StatusPill>
        ) : null}
      </button>

      {open ? (
        <div className="flex flex-col gap-3 border-t border-borderSubtle p-3">
          {/* The stages, which is the answer to "what did it actually do, and what was slow". */}
          <div>
            <h4 className="mb-1 text-label-md uppercase tracking-wide text-textMuted">
              {t("dev.stages")}
            </h4>
            <ul className="flex flex-col gap-1">
              {turn.stages.map((stage, index) => (
                <li key={`${stage.name}-${index}`} className="flex items-baseline gap-3 text-body-sm">
                  <span
                    className={
                      stage.name === turn.slowestStage
                        ? "w-24 font-mono text-statusWarning"
                        : "w-24 font-mono"
                    }
                  >
                    {stage.name}
                  </span>
                  <span className="w-20 text-end font-mono tabular-nums text-textMuted">
                    {stage.ms} ms
                  </span>
                  <span className="text-textMuted">{stage.detail}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="mb-1 text-label-md uppercase tracking-wide text-textMuted">
              {t("dev.systemsTouched")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {turn.systems.map((system) => (
                <StatusPill key={system} tone="neutral">
                  {system}
                </StatusPill>
              ))}
            </div>
          </div>

          <dl className="grid gap-x-6 gap-y-1 text-body-sm sm:grid-cols-2">
            <Fact label={t("dev.auditId")} value={turn.auditId} mono />
            <Fact label={t("dev.sessionId")} value={turn.sessionId} mono />
            <Fact label={t("dev.requestId")} value={turn.requestId} mono />
            <Fact label={t("dev.configVersion")} value={turn.configVersion} mono />
            <Fact
              label={t("dev.language")}
              value={`${turn.language}-${turn.script}`}
            />
            <Fact
              label={t("dev.confidence")}
              value={turn.routerConfidence?.toFixed(2) ?? null}
            />
            <Fact label={t("dev.validation")} value={turn.validation} mono />
            <Fact label={t("dev.refusal")} value={turn.refusalReason} />
            <Fact label={t("dev.triggerId")} value={turn.triggerId} mono />
            <Fact
              label={t("dev.collections")}
              value={turn.collectionsSearched.join(", ") || null}
              mono
            />
            <Fact label={t("dev.offer")} value={turn.offerId} />
            <Fact
              label={t("dev.sessionReset")}
              value={turn.sessionReset ? t("dev.yes") : null}
            />
          </dl>

          {turn.parameters ? (
            <div>
              <h4 className="mb-1 text-label-md uppercase tracking-wide text-textMuted">
                {t("dev.parameters")}
              </h4>
              <pre className="overflow-x-auto rounded-sm bg-surfaceSunken p-3 font-mono text-label-md">
                {JSON.stringify(turn.parameters, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function Fact({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}): JSX.Element | null {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="text-textMuted">{label}</dt>
      <dd className={mono ? "font-mono text-label-md" : ""}>{value}</dd>
    </div>
  );
}
